<template>
  <Card
    :id="id"
    class="open-ship-order-card"
    :aria-labelledby="`${id}-header`"
    :class="{ 'cursor-grab': draggable }"
    :data-ship-order-id="shipOrder.id"
    :draggable="draggable"
    tabindex="-1"
  >
    <template #content>
      <div class="top-row flex justify-content-between align-items-center">
        <div class="top-row-info flex flex-gap-1">
          <h3
            :id="`${id}-header`"
            class="open-ship-order-card-header"
            tabindex="-1"
          >
            {{
              t('orders.orderNumParam', {
                param: `${shipOrder.orderId}-${shipOrder.id}`,
              })
            }}
          </h3>
          <span class="text-gray-darkest">
            <FontAwesomeIcon
              aria-hidden="true"
              class="text-gray-darker"
              :icon="['fas', 'weight-hanging']"
            />
            {{
              translateNumberedEntity(
                totalWeight,
                `uoms.weight.abbreviations.POUND`,
              )
            }}
          </span>
          <span class="text-gray-darkest">
            <FontAwesomeIcon
              aria-hidden="true"
              class="text-gray-darker"
              :icon="['fas', 'list']"
            />
            {{ totalHandlingUnits }}
            {{ handlingUnits }}
          </span>
        </div>

        <RygenButton
          v-if="addable"
          v-tooltip="{ value: t('multistop.addToShipment') }"
          mode="tertiary"
          :aria-label="t('multistop.addToShipment')"
          @click="$emit('add-ship-order', shipOrder)"
        >
          <FontAwesomeIcon :icon="['far', 'truck-arrow-right']" />
        </RygenButton>
        <EllipsisMenu :menu-items="menuItems" :disabled="!menuItems.length" />
      </div>

      <div
        class="additional-row flex flex-gap-1 justify-content-between align-items-center text-gray-darkest"
      >
        <div
          class="open-ship-order-shipping-point-info open-ship-order-shipping-point-info-origin"
        >
          <NamedAddress
            :name="shipOrder?.origin?.location?.locationName"
            :address="shipOrder?.origin?.location?.toAddress()"
            suppress-address-lines
            supress-postal-code
          />
          <div>
            {{ originDate }}
          </div>
        </div>
        <FontAwesomeIcon
          aria-hidden="true"
          class="text-gray-darker"
          :icon="['fal', 'long-arrow-right']"
        />
        <div
          class="open-ship-order-shipping-point-info open-ship-order-shipping-point-info-destination"
        >
          <NamedAddress
            :name="shipOrder?.destination?.location?.locationName"
            :address="shipOrder?.destination?.location?.toAddress()"
            suppress-address-lines
            supress-postal-code
          />
          <div>
            {{ destinationDate }}
          </div>
        </div>
      </div>

      <div
        v-if="shipOrder.swc || shipOrder.customerRouted"
        class="additional-row flex flex-gap-1 align-items-center text-gray-darkest"
      >
        <SwcChip v-if="shipOrder.swc" />
        <CustomerRoutedChip v-if="shipOrder.customerRouted" />
      </div>

      <div
        v-if="shipOrder.tags.length"
        class="additional-row flex flex-gap-1 align-items-center text-gray-darkest"
      >
        <!--
          TODO: Once they can see tags, users are gonna wanna search on tags.
          When we add a tag filter here, add a `route-builder` prop that applies
          a search for the cliecked tag
        -->
        <TagContainer v-model="shipOrder.tags" readonly />
      </div>

      <ShipOrderSplitModal
        v-model:visible="shipOrderSplitModalVisible"
        :ship-order="shipOrder"
        @refresh-candidates="$emit('refresh-candidates')"
      />
    </template>
  </Card>
</template>

<script lang="ts" setup>
import { DateTime } from 'luxon';
import type { Ref } from 'vue';
import { computed, ref, toRef, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { useTooltip } from '@rygen/rygen-ui/composables';
import { renderDateTime, translateNumberedEntity } from '@rygen/rygen-ui/i18n';
import { RygenMenuItem } from '@rygen/rygen-ui/model';

import { useLineItemsSummary } from '@/composables';
import { ShipOrder } from '@/model';

import ShipOrderSplitModal from '@/components/shipmentPlanning/consolidation/ShipOrderSplitModal.vue';
import CustomerRoutedChip from '@/components/ui/CustomerRoutedChip.vue';
import NamedAddress from '@rygen/rygen-ui/components/address/NamedAddress.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import SwcChip from '@rygen/rygen-ui/components/swc/SwcChip.vue';
import TagContainer from '@rygen/rygen-ui/components/tag/TagContainer.vue';
import EllipsisMenu from '@rygen/rygen-ui/components/ui/EllipsisMenu.vue';
import Card from 'primevue/card';

const props = defineProps({
  shipOrder: {
    type: ShipOrder,
    default: () => new ShipOrder(),
  },
  draggable: {
    type: Boolean,
    default: () => false,
  },
  /**
   * Indicates if the shiporder is on the proposed shipment and can be removed
   **/
  removable: {
    type: Boolean,
    default: () => false,
  },
  /**
   * Indicates if the shiporder is *not* on the proposed shipent and can be
   * added to it
   **/
  addable: {
    type: Boolean,
    default: () => false,
  },
});
const { shipOrder, draggable, removable, addable } = toRefs(props);
const emit = defineEmits<{
  (event: 'remove', shipOrder: ShipOrder): void;
  (event: 'add-ship-order', shipOrder: ShipOrder): void;
  (event: 'refresh-candidates'): void;
}>();

const lineItems = toRef(() => shipOrder.value.lineItems);

const { t } = useI18n();
const vTooltip = useTooltip();

const { handlingUnits, totalHandlingUnits, totalWeight } =
  useLineItemsSummary(lineItems);

const shipOrderSplitModalVisible = ref(false);

const id = computed(() => `open-ship-order-${shipOrder.value.id}`);

const renderDate = (
  startDate: DateTime | undefined,
  endDate: DateTime | undefined,
) => {
  const start = renderDateTime(startDate, { timeFormat: 'NONE' });
  const end = renderDateTime(endDate, { timeFormat: 'NONE' });

  // Handle edge cases where one or both dates are missing
  if (!startDate && !endDate) {
    return '-';
  } else if (!startDate && !!endDate) {
    return end;
  } else if (!endDate && !!startDate) {
    return start;
  }

  // Return only one date where it is the same calendar day
  if (start === end) {
    return start;
  }

  return `${start} - ${end}`;
};

const originDate = computed(() =>
  renderDate(
    shipOrder.value.origin?.requestedAppointmentEarliest,
    shipOrder.value.origin?.requestedAppointmentLatest,
  ),
);

const destinationDate = computed(() =>
  renderDate(
    shipOrder.value.destination?.requestedAppointmentEarliest,
    shipOrder.value.destination?.requestedAppointmentLatest,
  ),
);

const menuItems: Ref<RygenMenuItem[]> = computed(() => [
  {
    label: t('common.viewParam', { param: t('orders.orders') }),
    icon: 'pi pi-eye',
    to: { name: 'orderDetail', params: { orderId: shipOrder.value.orderId } },
  },
  {
    label: t('shipOrders.shipOrderSplit.split'),
    icon: 'pi pi-arrows-h',
    command: () => (shipOrderSplitModalVisible.value = true),
    visible: addable.value,
  },
  {
    label: t('common.remove'),
    class: 'text-danger',
    icon: 'pi pi-times',
    command: () => emit('remove', shipOrder.value),
    visible: removable.value,
  },
]);
</script>

<style>
.open-ship-order-card.p-card {
  border: 1px solid var(--p-gray-200);

  &.cursor-grab {
    cursor: grab;
  }

  .p-card-body {
    padding: 0;

    .top-row {
      .top-row-info {
        padding: var(--sp-3);
        margin-right: auto;

        .open-ship-order-card-header {
          color: var(--p-gray-600);
          font-size: 14px;
          font-weight: 700;
        }
      }
    }

    .additional-row {
      padding: var(--sp-3);
      background-color: var(--p-gray-200);
      font-size: 12px;
      font-weight: 400;

      & + .additional-row {
        padding-top: 0;
      }

      .pi.pi-arrow-right {
        transform: scaleX(150%);
      }

      .open-ship-order-shipping-point-info {
        .named-address {
          max-width: 200px;
        }
      }
    }
  }
}
</style>
